import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './components/ui';
import { useLearningTimeTracker } from './hooks/useLearningTimeTracker';
import './App.css';

// Ленивая загрузка страниц; Login и Register — обычный импорт, чтобы избежать ChunkLoadError на экранах входа
import Login from './components/Login';
import Register from './components/Register';
import { readOnboardingPending } from './components/AuthShell';
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const Users = lazy(() => import('./components/Users'));
const Library = lazy(() => import('./components/Library'));
const StudyTools = lazy(() => import('./components/StudyTools'));
const MeaningRecognitionMode = lazy(() => import('./components/MeaningRecognitionMode'));
const SpeechContextTrainer = lazy(() => import('./components/SpeechContextTrainer'));
const Achievements = lazy(() => import('./components/Achievements'));
const Settings = lazy(() => import('./components/Settings'));
const Friends = lazy(() => import('./components/Friends'));
const Lessons = lazy(() => import('./components/Lessons'));
const Rules = lazy(() => import('./components/Rules'));
const PlanReport = lazy(() => import('./components/plan/PlanReport'));
const PlanGoal = lazy(() => import('./components/plan/PlanGoal'));
const Profile = lazy(() => import('./components/Profile'));
const Onboarding = lazy(() => import('./components/Onboarding'));
const StreakPage = lazy(() => import('./components/game/GamePages').then((m) => ({ default: m.StreakPage })));
const QuestsPage = lazy(() => import('./components/game/GamePages').then((m) => ({ default: m.QuestsPage })));

const PageLoader = () => <div className="loading" aria-label="Загрузка" />;

const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();
  // Heartbeat трекер времени в приложении — учитывает только пока окно
  // видимое + сфокусированное. Сам хук no-op для незалогиненных юзеров.
  useLearningTimeTracker();

  if (loading) {
    return (
      <div className="loading" />
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" />} />
        <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to={readOnboardingPending() ? '/onboarding' : '/dashboard'} replace />} />
        <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" />} />
        <Route path="/reset-password" element={!isAuthenticated ? <ResetPassword /> : <Navigate to="/dashboard" />} />
        <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
        <Route path="/users" element={<AdminRoute><Users /></AdminRoute>} />
        <Route path="/words" element={<PrivateRoute><Library /></PrivateRoute>} />
        <Route path="/study-tools" element={<PrivateRoute><StudyTools /></PrivateRoute>} />
        <Route path="/study-tools/meaning" element={<PrivateRoute><MeaningRecognitionMode /></PrivateRoute>} />
        <Route path="/study-tools/speech" element={<PrivateRoute><SpeechContextTrainer /></PrivateRoute>} />
        <Route path="/plan" element={<PrivateRoute><PlanReport /></PrivateRoute>} />
        <Route path="/plan/goal" element={<PrivateRoute><PlanGoal /></PrivateRoute>} />
        <Route path="/achievements" element={<PrivateRoute><Achievements /></PrivateRoute>} />
        {/* v2: лидерборд объединён с друзьями — одна страница «Друзья и рейтинг» */}
        <Route path="/leaderboard" element={<Navigate to="/friends" replace />} />
        <Route path="/rules/*" element={<PrivateRoute><Rules /></PrivateRoute>} />
        {/* v2: страница «Наборы» убрана — наборы живут блоком на Главной */}
        <Route path="/sets" element={<Navigate to="/dashboard#sets" replace />} />
        <Route path="/sentences" element={<Navigate to="/words?tab=phrases" replace />} />
        <Route path="/lessons" element={<PrivateRoute><Lessons /></PrivateRoute>} />
        <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
        <Route path="/friends" element={<PrivateRoute><Friends /></PrivateRoute>} />
        <Route path="/streak" element={<PrivateRoute><StreakPage /></PrivateRoute>} />
        <Route path="/quests" element={<PrivateRoute><QuestsPage /></PrivateRoute>} />
        <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
        <Route path="/onboarding" element={<PrivateRoute><Onboarding /></PrivateRoute>} />
        <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
